import { useCallback, useEffect, useState } from 'react';
import Modal, { Picker } from './Modal.jsx';
import Avatar from './Avatar.jsx';
import InterestEditor from './InterestEditor.jsx';
import LinkEditor from './LinkEditor.jsx';
import { api } from '../api.js';
import { randomSeed } from '../lib/avatar.js';
import ConsentToggle from './ConsentToggle.jsx';

const BIO_MAX = 500;

export default function ProfileModal({
  config, me, locationStatus, onRefreshLocation, onSaved, onSignOut, onClose,
  consent, onConsentChange,
}) {
  const secure = typeof window !== 'undefined' && window.isSecureContext && !!navigator.geolocation;
  const [username, setUsername] = useState(me.username);
  const [displayName, setDisplayName] = useState(me.displayName);
  const [bio, setBio] = useState(me.bio || '');
  const [interests, setInterests] = useState(me.interests || []);
  const [seed, setSeed] = useState(me.seed);
  const [color, setColor] = useState(me.color);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // One fetch for both editors below. Reading a link produces the tags the
  // interest editor offers, so they cannot each own their own copy of it.
  const [links, setLinks] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [linksLoaded, setLinksLoaded] = useState(false);

  const reloadLinks = useCallback(async () => {
    try {
      const data = await api.links();
      setLinks(data.links ?? []);
      setSuggested(data.suggested ?? []);
    } catch { /* the sections simply stay empty */ }
    setLinksLoaded(true);
  }, []);

  useEffect(() => { reloadLinks(); }, [reloadLinks]);

  async function submit(ev) {
    ev.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { user } = await api.updateProfile({
        username, displayName, bio, interests, seed, color,
      });
      onSaved(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const locationLine = {
    located: 'Shown as an approximate area, about 5 km across',
    locating: 'Finding this device…',
    denied: 'Location blocked — allow it to appear on the globe',
    unavailable: 'Device location unavailable; an approximate one is used',
  }[locationStatus] ?? '';
  const locationHint = secure ? '' : 'Device location requires HTTPS or localhost.';

  return (
    <Modal onClose={onClose}>
      {/* The face and its colour are edited where the face is, rather than in
          two more labelled fields further down. */}
      <div className="profile-head">
        <div className="profile-face">
          <Avatar person={{ seed, color, displayName }} size="lg" />
          <button
            type="button" className="face-reroll" title="Another face"
            aria-label="Another face" onClick={() => setSeed(randomSeed())}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" />
            </svg>
          </button>
        </div>
        <div className="profile-ident">
          <h2>{displayName || me.username}</h2>
          <Picker options={config.colors} value={color} onChange={setColor} kind="color" />
        </div>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}

      <form onSubmit={submit}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="pd-name">Name</label>
            <input
              id="pd-name" value={displayName} maxLength={40}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pd-handle">Handle</label>
            <div className="handle-input">
              <span className="handle-prefix" aria-hidden="true">@</span>
              <input
                id="pd-handle"
                value={username}
                maxLength={20}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              />
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="pd-bio">Bio</label>
          <textarea
            id="pd-bio"
            className="bio-input"
            value={bio}
            rows={3}
            maxLength={BIO_MAX}
            placeholder="Tell people about yourself. Links are clickable."
            onChange={(e) => setBio(e.target.value)}
          />
          <div className="bio-meta"><span /><p className="hint">{bio.length}/{BIO_MAX}</p></div>
        </div>

        <div className="field">
          <label htmlFor="pd-interests">Interests</label>
          <InterestEditor
            id="pd-interests"
            value={interests}
            onChange={setInterests}
            suggested={suggested}
          />
        </div>

        {/* Links save themselves as they are added — the server has to go and
            read each one, and Save should not wait on someone else's site. */}
        <div className="field">
          <label>
            Links
            <span className="field-optional">saved as you add them</span>
          </label>
          <LinkEditor links={links} loaded={linksLoaded} onReload={reloadLinks} />
        </div>

        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Saving…' : 'Save profile'}
        </button>
      </form>

      {/* Where you appear and signing out are account business, not profile
          fields. One quiet row rather than two more sections. */}
      <div className="profile-foot">
        <span className="profile-foot-notes">
          <span className="hint" title={locationHint}>{locationLine}</span>
          {/* Answering the bar once used to be the end of it. A choice you
              cannot revisit is not much of a choice, and this is where the
              other thing you are told about yourself already sits. */}
          {onConsentChange && (
            <ConsentToggle
              consent={consent} onChange={onConsentChange} verbose
              className="consent-toggle"
            />
          )}
        </span>
        <span className="profile-foot-actions">
          <button
            type="button" className="btn btn-sm" onClick={onRefreshLocation} disabled={!secure}
            title={secure ? 'Ask this device for its current coordinates' : 'Browsers require HTTPS or localhost'}
          >
            Refresh
          </button>
          <button type="button" className="btn btn-sm" onClick={onSignOut}>Sign out</button>
        </span>
      </div>
    </Modal>
  );
}
